/* ============================================================
   APRENZ — global.js
   Cart (AJAX drawer), free-shipping bar, sticky ATC, quiz,
   PDP subscription selector, gallery dots, toast.
   No framework — vanilla JS, progressive enhancement.
   ============================================================ */
(function () {
  'use strict';

  const money = (cents) =>
    (window.Shopify && window.Shopify.formatMoney)
      ? window.Shopify.formatMoney(cents, window.themeMoneyFormat)
      : '$' + (cents / 100).toFixed(2);

  /* ---------- toast ---------- */
  let toastTimer;
  function toast(msg) {
    const t = document.getElementById('toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('show'), 1800);
  }
  window.toast = toast;

  /* ============================================================
     CART
     ============================================================ */
  const Cart = {
    get drawer() { return document.querySelector('.cart-drawer'); },
    get overlay() { return document.querySelector('.cart-overlay'); },

    open() {
      this.drawer && this.drawer.classList.add('open');
      this.overlay && this.overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
    },
    close() {
      this.drawer && this.drawer.classList.remove('open');
      this.overlay && this.overlay.classList.remove('open');
      document.body.style.overflow = '';
    },

    async add(id, qty, selling_plan) {
      const body = { items: [{ id: Number(id), quantity: qty || 1 }] };
      if (selling_plan) body.items[0].selling_plan = Number(selling_plan);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast(err.description || 'could not add — out of stock?');
          return;
        }
        await this.refresh();
        this.open();
        toast('added →');
      } catch (e) {
        toast('network error');
      }
    },

    async change(key, qty) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      const cart = await res.json();
      this.render(cart);
      this.updateCount(cart.item_count);
      return cart;
    },

    async refresh() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.render(cart);
      this.updateCount(cart.item_count);
      return cart;
    },

    updateCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = n;
        el.hidden = n === 0;
      });
    },

    render(cart) {
      const wrap = document.querySelector('[data-cart-items]');
      if (!wrap) return;
      if (!cart.items.length) {
        wrap.innerHTML = '<div class="cart-empty">your bag is empty.<br>go find your stack.</div>';
      } else {
        wrap.innerHTML = cart.items.map((it) => {
          const img = it.image
            ? `<img src="${it.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_120x120$1$2')}" alt="${it.title}">`
            : 'IMG';
          const opt = it.selling_plan_allocation
            ? it.selling_plan_allocation.selling_plan.name
            : (it.variant_title || '');
          return `
            <div class="citem" data-key="${it.key}">
              <div class="thumb">${img}</div>
              <div class="meta">
                <h4>${it.product_title.toLowerCase()}</h4>
                ${opt ? `<div class="opt">${opt}</div>` : ''}
                <div class="line">
                  <div class="qty">
                    <button data-qty-down aria-label="decrease">&minus;</button>
                    <input type="text" value="${it.quantity}" data-qty-input readonly>
                    <button data-qty-up aria-label="increase">+</button>
                  </div>
                  <span class="price">${money(it.final_line_price)}</span>
                </div>
                <button class="remove" data-remove>remove</button>
              </div>
            </div>`;
        }).join('');
      }

      // subtotal
      const sub = document.querySelector('[data-cart-subtotal]');
      if (sub) sub.textContent = money(cart.total_price);

      this.shipBar(cart.total_price);
    },

    shipBar(total) {
      const bar = document.querySelector('[data-ship-bar]');
      if (!bar) return;
      const threshold = Number(bar.dataset.threshold || 5000);
      const msg = bar.querySelector('[data-ship-msg]');
      const fill = bar.querySelector('[data-ship-fill]');
      const pct = Math.min(100, Math.round((total / threshold) * 100));
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        if (total >= threshold) {
          msg.innerHTML = '<b>you unlocked free shipping</b> 🎉';
        } else {
          msg.innerHTML = `you’re <b>${money(threshold - total)}</b> away from free shipping`;
        }
      }
    },
  };
  window.Cart = Cart;

  /* delegate cart interactions */
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-cart-open]');
    if (openBtn) { e.preventDefault(); Cart.refresh().then(() => Cart.open()); return; }

    if (e.target.closest('[data-cart-close]') || e.target.classList.contains('cart-overlay')) {
      Cart.close(); return;
    }

    // quick-add / add-to-cart buttons anywhere
    const add = e.target.closest('[data-add-to-cart]');
    if (add) {
      e.preventDefault();
      e.stopPropagation();
      const planEl = document.querySelector('[data-selling-plan]:checked, [data-selling-plan].sel');
      const plan = add.dataset.sellingPlan || (planEl && planEl.dataset.sellingPlan) || null;
      Cart.add(add.dataset.variantId, Number(add.dataset.qty || 1), plan);
      return;
    }

    const item = e.target.closest('.citem');
    if (!item) return;
    const key = item.dataset.key;
    const input = item.querySelector('[data-qty-input]');
    if (e.target.closest('[data-qty-up]')) Cart.change(key, Number(input.value) + 1);
    if (e.target.closest('[data-qty-down]')) Cart.change(key, Math.max(0, Number(input.value) - 1));
    if (e.target.closest('[data-remove]')) Cart.change(key, 0);
  });

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') Cart.close(); });

  /* ============================================================
     STICKY ATC (PDP) — show once the main add-to-cart scrolls away
     ============================================================ */
  function initStickyAtc() {
    const sticky = document.getElementById('stickyatc');
    const anchor = document.getElementById('atc-anchor');
    if (!sticky || !anchor || !('IntersectionObserver' in window)) {
      if (sticky) sticky.classList.add('show');
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => sticky.classList.toggle('show', !entry.isIntersecting),
      { rootMargin: '0px 0px -80px 0px' }
    );
    io.observe(anchor);
  }

  /* ============================================================
     PDP — subscription / one-time selector
     ============================================================ */
  function initSubSelector() {
    const sel = document.querySelector('[data-subsel]');
    if (!sel) return;
    sel.addEventListener('click', (e) => {
      const opt = e.target.closest('.subopt');
      if (!opt) return;
      sel.querySelectorAll('.subopt').forEach((o) => o.classList.remove('sel'));
      opt.classList.add('sel');
      // sync sticky ATC price + label + selling plan on the add buttons
      const price = opt.dataset.price;
      const label = opt.dataset.label;
      const plan = opt.dataset.sellingPlan || '';
      document.querySelectorAll('[data-add-to-cart]').forEach((b) => {
        if (plan) b.dataset.sellingPlan = plan; else delete b.dataset.sellingPlan;
      });
      const sp = document.querySelector('#stickyatc .pr b');
      const sl = document.querySelector('#stickyatc .pr span');
      if (sp && price) sp.textContent = price;
      if (sl && label) sl.textContent = label;
    });
  }

  /* ============================================================
     PDP gallery dots
     ============================================================ */
  function initGallery() {
    const slides = document.querySelector('.pdpgallery .slides');
    const dots = document.querySelectorAll('.pdpgallery .dots i');
    if (!slides || !dots.length) return;
    slides.addEventListener('scroll', () => {
      const idx = Math.round(slides.scrollLeft / slides.clientWidth);
      dots.forEach((d, i) => d.classList.toggle('on', i === idx));
    });
  }

  /* ============================================================
     STACK QUIZ — client-side, questions/answers come from data attrs
     ============================================================ */
  function initQuiz() {
    const quiz = document.querySelector('[data-quiz]');
    if (!quiz) return;
    const steps = quiz.querySelectorAll('.qstep');
    const progress = quiz.querySelectorAll('.qprog span');
    const result = quiz.querySelector('.qresult');
    let answers = {};

    function showStep(n) {
      steps.forEach((s) => s.classList.toggle('active', Number(s.dataset.step) === n));
      progress.forEach((p, i) => p.classList.toggle('on', i <= n));
    }

    quiz.addEventListener('click', (e) => {
      const opt = e.target.closest('.qopt[data-value]');
      if (opt) {
        const step = Number(opt.closest('.qstep').dataset.step);
        answers[step] = opt.dataset.value;
        if (step < steps.length - 1) {
          showStep(step + 1);
        } else {
          renderResult();
        }
        return;
      }
      if (e.target.closest('[data-quiz-reset]')) {
        answers = {};
        result.classList.remove('show');
        showStep(0);
      }
    });

    function renderResult() {
      steps.forEach((s) => s.classList.remove('active'));
      // result map keyed by the step-0 answer; stored as JSON on the quiz element
      let map = {};
      try { map = JSON.parse(quiz.dataset.resultMap || '{}'); } catch (e) {}
      const key = answers[0];
      const r = map[key] || map[Object.keys(map)[0]] || {};
      const setText = (sel, val) => { const el = result.querySelector(sel); if (el && val != null) el.textContent = val; };
      setText('[data-r-name]', r.name);
      setText('[data-r-products]', r.products);
      setText('[data-r-desc]', r.desc);
      const cta = result.querySelector('[data-r-cta]');
      if (cta && r.url) cta.href = r.url;
      const img = result.querySelector('[data-r-img]');
      if (img && r.image) img.innerHTML = `<img src="${r.image}" alt="${r.name || ''}">`;
      result.classList.add('show');
    }
  }

  /* ============================================================
     PDP — variant picker (updates variant id + price on all ATC buttons)
     ============================================================ */
  function initVariantPicker() {
    const picker = document.querySelector('[data-variant-select]');
    if (!picker) return;
    let variants = [];
    try { variants = JSON.parse(picker.dataset.variants || '[]'); } catch (e) {}
    picker.addEventListener('change', () => {
      const v = variants.find((x) => String(x.id) === String(picker.value));
      if (!v) return;
      document.querySelectorAll('[data-add-to-cart]').forEach((b) => {
        b.dataset.variantId = v.id;
        b.disabled = !v.available;
        if (!v.available) b.textContent = 'sold out';
      });
      const main = document.querySelector('[data-price-main]');
      if (main) main.textContent = v.price;
    });
  }

  /* ============================================================
     Announcement bar rotator
     ============================================================ */
  function initAnnouncement() {
    const bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    const msgs = bar.querySelectorAll('.ann-msg');
    if (msgs.length < 2) return;
    const speed = Number(bar.dataset.speed) || 4000;
    let i = 0;
    setInterval(() => {
      msgs[i].classList.remove('on');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('on');
    }, speed);
  }

  /* ============================================================
     init
     ============================================================ */
  document.addEventListener('DOMContentLoaded', () => {
    initStickyAtc();
    initSubSelector();
    initVariantPicker();
    initGallery();
    initQuiz();
    initAnnouncement();
  });
})();
